<template>
	<div v-if="isSolutionClass" class="p-p-2">
		<Message severity="info" :closable="false">{{ $t('Squeeze.DocumentClasses.SystemPromptNotForSolutionClasses') }}</Message>
	</div>
	<div v-else class="p-p-2 systemPromptContainer">
		<BlockUI :blocked="loading" class="promptArea">
			<p class="description">{{ $t('Squeeze.DocumentClasses.SystemPromptDescription') }}</p>
			<Editor
				id="systemPrompt"
				class="systemPromptEditor"
				:placeholder="$t('Squeeze.DocumentClasses.SystemPromptPlaceholder')"
				@load="onEditorLoad"
				@text-change="onTextChange"
			>
				<template #toolbar>
					<span class="ql-formats">
						<button class="ql-list" value="ordered" type="button"></button>
						<button class="ql-list" value="bullet" type="button"></button>
					</span>
					<span class="ql-formats toolbarDivider">
						<button class="ql-clean" type="button"></button>
					</span>
				</template>
			</Editor>
		</BlockUI>

		<div class="p-fluid p-formgrid p-grid p-jc-end p-pt-2 p-px-2 buttonBar">
			<div class="p-field p-col-3">
				<Button
					:disabled="loading || !isDirty"
					:label="$t('Squeeze.General.Reset')"
					type="button"
					icon="mdi mdi-restore"
					class="p-button-secondary"
					@click="resetSystemPrompt"
				/>
			</div>
			<div class="p-field p-col-3">
				<Button
					:disabled="loading || !isDirty"
					:label="$t('Squeeze.General.Save')"
					type="button"
					icon="mdi mdi-content-save-outline"
					class="p-button"
					@click="saveSystemPrompt"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, shallowRef, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import BlockUI from "primevue/blockui";
import Editor, {EditorLoadEvent} from "primevue/editor";
import Message from "primevue/message";
import {ToastManager} from "@/util/ToastManager";
import {ClientManager} from "@/singletons/ClientManager";
import type Quill from "quill";
import type {DeltaStatic} from "quill";

export default defineComponent({
	name: "DocumentClassSystemPromptSubView",
	components: {
		BlockUI,
		Editor,
		Message,
	},
	props: {
		documentClassId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();

		/** Service for Document-Classes */
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;

		/** Quill instance of the editor */
		const quill = shallowRef<Quill | null>(null);

		/** Current editor content, serialized as a Quill document */
		const systemPrompt = ref<string>('');

		/** Last value loaded from the API */
		const savedSystemPrompt = ref<string>('');

		/** Is the page currently loading or saving? */
		const loading = ref<boolean>(false);

		/** Solution class (predefined by DEXPRO) are not editable? */
		const isSolutionClass = ref<boolean>(false);

		/** Are there unsaved changes? */
		const isDirty = computed<boolean>(() => systemPrompt.value !== savedSystemPrompt.value);

		/** Gets the system prompt of the current document class */
		const getSystemPrompt = () => {
			if (!props.documentClassId) {
				return;
			}

			loading.value = true;

			documentClassService.getDocumentClassSystemPrompt(props.documentClassId)
				.then(data => {
					savedSystemPrompt.value = data.systemPrompt ?? '';
					systemPrompt.value = savedSystemPrompt.value;
					isSolutionClass.value = false;
					showInEditor(savedSystemPrompt.value);
				})
				.catch((err: { statusText: string; status?: number }) => {
					if (err.status === 403) {
						isSolutionClass.value = true;
						return;
					}

					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.DocumentClasses.SystemPrompt') + ": " + err.statusText);
				})
				.finally(() => {
					loading.value = false;
				});
		}

		/** Saves the system prompt of the current document class */
		const saveSystemPrompt = () => {
			if (!props.documentClassId) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.SaveError'));
				return;
			}

			loading.value = true;

			// An empty editor means "no custom prompt"
			const valueToSave = systemPrompt.value === '' ? undefined : systemPrompt.value;

			documentClassService.putDocumentClassSystemPrompt(props.documentClassId, {systemPrompt: valueToSave})
				.then(() => {
					savedSystemPrompt.value = valueToSave ?? '';
					systemPrompt.value = savedSystemPrompt.value;
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess'));
				})
				.catch((err: { statusText: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.SaveError') + ": " + err.statusText);
				})
				.finally(() => {
					loading.value = false;
				});
		}

		/** Discards all unsaved changes */
		const resetSystemPrompt = () => {
			systemPrompt.value = savedSystemPrompt.value;
			showInEditor(savedSystemPrompt.value);
		}

		/** Keeps the editor instance and shows a prompt that was already loaded */
		const onEditorLoad = (event: EditorLoadEvent) => {
			quill.value = event.instance as Quill;

			if (savedSystemPrompt.value) {
				showInEditor(savedSystemPrompt.value);
				return;
			}

			clearEditor();
		}

		/** Reads the editor content back as a Quill document whenever the user types */
		const onTextChange = () => {
			if (!quill.value) {
				return;
			}

			systemPrompt.value = isEditorEmpty() ? '' : JSON.stringify(quill.value.getContents());
		}

		/**
		 * Puts a stored prompt into the editor.
		 *
		 * @param {string} value Stored prompt, a serialized Quill document or plain text
		 */
		const showInEditor = (value: string) => {
			if (!quill.value) {
				// The editor has not loaded yet
				return;
			}

			if (!value) {
				clearEditor();
				return;
			}

			try {
				const contents = JSON.parse(value);
				if (contents && Array.isArray(contents.ops)) {
					if (contents.ops.length === 0) {
						clearEditor();
						return;
					}

					quill.value.setContents(contents as DeltaStatic);
					return;
				}
			} catch {
				// Not a Quill document, so it is treated as plain text below.
			}

			quill.value.setText(value);
		}

		/**
		 * Clears the editor, linebreak needed otherwise editor breaks
		 */
		const clearEditor = () => {
			quill.value?.setText('\n');
		}

		/** When no text is inserted, prompt not saved / Cleared on API side to fallback to default system default prompt */
		const isEditorEmpty = (): boolean => {
			return (quill.value?.getText() ?? '').trim() === '';
		}

		onMounted(() => {
			getSystemPrompt();
		})

		watch(() => props.documentClassId, () => {
			getSystemPrompt();
		})

		return {
			systemPrompt,
			loading,
			isSolutionClass,
			isDirty,
			getSystemPrompt,
			saveSystemPrompt,
			resetSystemPrompt,
			onEditorLoad,
			onTextChange,
		};
	},
});
</script>

<style lang="scss" scoped>

.description {
	margin: 0 0 1rem 0;
}

/* Fills the space below the tab menu, so only the editor scrolls and the buttons stay at the bottom */
.systemPromptContainer {
	display: flex;
	flex-flow: column nowrap;
	height: calc(100vh - 5.6rem - 3.538rem - 0.5rem);
}

.promptArea {
	display: flex;
	flex-flow: column nowrap;
	flex: 1 1 auto;
	min-height: 0;
}

.buttonBar {
	flex: 0 0 auto;
}

.buttonBar .p-field {
	margin-bottom: 0;
}

.systemPromptEditor {
	display: flex;
	flex-flow: column nowrap;
	flex: 1 1 auto;
	min-height: 0;
}

.systemPromptEditor:deep(.p-editor-content) {
	height: auto;
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}

.systemPromptEditor:deep(.ql-editor) {
	min-height: 100%;
}

/* Border of the standard input fields instead of the lighter default one of the editor */
.systemPromptEditor:deep(.p-editor-toolbar.ql-snow),
.systemPromptEditor:deep(.p-editor-content.ql-snow) {
	border-color: rgba(0, 0, 0, 0.38);
}

/* Divider between the toolbar groups */
.systemPromptEditor:deep(.ql-toolbar.ql-snow .ql-formats) {
	margin-right: 0;
}

.systemPromptEditor:deep(.ql-toolbar.ql-snow .ql-formats.toolbarDivider) {
	margin-left: 0.5rem;
	padding-left: 0.5rem;
	border-left: 1px solid var(--surface-300);
}

</style>
